import React from 'react';
import { 
  Plus, Trash2, Edit2, RotateCcw, Home, DollarSign, Maximize2, Layers, 
  Heart, Sparkles, Image as ImageIcon, Users, Mail, Phone, Briefcase, 
  RefreshCw, ArrowLeft, Palette, Search, Cloud, Video, Database, LogOut, 
  ShieldCheck, HardDrive, Check, ArrowUp, ArrowDown, GripVertical, ListOrdered, Star,
  Eye, EyeOff 
} from 'lucide-react';
import { Apartment, Agent } from '../types';
import { STOCK_GALLERY_IMAGES } from '../data/initialData';
import { AGENCY_CONFIG } from '../config/agency';
import { ESTATE_BROKERS, findBrokerByNameOrId } from '../data/estateBrokers';
import { useAuth } from '../context/AuthContext';
import { useSiteContent } from '../context/SiteContentContext';
import SyncManager from './SyncManager';
import SiteContentEditor from './admin/SiteContentEditor';
import SEOManager from './admin/SEOManager';
import FirebaseManager from './admin/FirebaseManager';
import InquiriesManager from './admin/InquiriesManager';
import MediaLibrary from './admin/MediaLibrary';
import { MediaPickerModal } from './admin/MediaPickerModal';
import { getPositionedImageStyle } from '../utils/imageStyle';
import AgentCardLivePreview from './admin/AgentCardLivePreview';
import { ESTATE_ASSISTANT_CATEGORIES } from '../data/categories';

const DEFAULT_AGENT_AVATAR = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%2394a3b8'%3E%3Cpath d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z'/%3E%3C/svg%3E";

interface AdminPanelProps {
  apartments: Apartment[];
  onAddProperty: (property: Apartment) => void;
  onEditProperty: (property: Apartment) => void;
  onDeleteProperty: (id: string) => void;
  onResetProperties?: () => void;
  onReorderProperties?: (properties: Apartment[]) => void;
  onToggleStatus?: (id: string) => void;
  neighborhoods: string[];
  agents: Agent[];
  onAddAgent: (agent: Agent) => void;
  onEditAgent: (agent: Agent) => void;
  onDeleteAgent: (id: string) => void;
  onResetAgents?: () => void;
  onReorderAgents?: (agents: Agent[]) => void;
  onExitAdmin?: () => void;
  onRefreshProperties?: () => void;
}

export default function AdminPanel({
  apartments,
  onAddProperty,
  onEditProperty,
  onDeleteProperty,
  onResetProperties,
  onReorderProperties,
  onToggleStatus,
  neighborhoods,
  agents,
  onAddAgent,
  onEditAgent,
  onDeleteAgent,
  onResetAgents,
  onReorderAgents,
  onExitAdmin,
  onRefreshProperties
}: AdminPanelProps) {
  const { user, signOut } = useAuth();
  const { content: siteContent, updateSection: updateSiteSection, saveContent: saveSiteContent } = useSiteContent();
  const [activeAdminTab, setActiveAdminTab] = React.useState<'properties' | 'agents' | 'images' | 'content' | 'seo' | 'sync' | 'firebase' | 'inquiries'>('properties');
  const [isFormOpen, setIsFormOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);

  // Custom Confirm Modal state
  const [confirmModal, setConfirmModal] = React.useState<{
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);

  // Image Adjustment state
  const [imageFilter, setImageFilter] = React.useState<'all' | 'properties' | 'agents'>('all');
  const [mediaTabMode, setMediaTabMode] = React.useState<'public_library' | 'adjust_crops'>('public_library');
  const [mediaPicker, setMediaPicker] = React.useState<{
    isOpen: boolean;
    onSelect: (url: string) => void;
    allowedType?: 'image' | 'video' | 'all';
    title?: string;
  }>({
    isOpen: false,
    onSelect: () => {},
    allowedType: 'all',
    title: 'Изберете медиен файл от public/'
  });
  const [adjustingItem, setAdjustingItem] = React.useState<{
    type: 'property' | 'agent';
    id: string;
    title: string;
    image: string;
    zoom: number;
    panX: number;
    panY: number;
    fit?: 'cover' | 'contain';
  } | null>(null);

  // Form states
  const [title, setTitle] = React.useState('');
  const [type, setType] = React.useState<'sale' | 'rent'>('sale');
  const [estateType, setEstateType] = React.useState('2-стаен');
  const [price, setPrice] = React.useState('');
  const [area, setArea] = React.useState('');
  const [rooms, setRooms] = React.useState('2');
  const [bathrooms, setBathrooms] = React.useState('1');
  const [floor, setFloor] = React.useState('3/6');
  const [neighborhood, setNeighborhood] = React.useState('Бриз');
  const [isCustomNeighborhood, setIsCustomNeighborhood] = React.useState(false);
  const [imageUrl, setImageUrl] = React.useState(STOCK_GALLERY_IMAGES[0]);
  const [description, setDescription] = React.useState('');
  const [tagsInput, setTagsInput] = React.useState('НОВА ОФЕРТА');
  const [brokerId, setBrokerId] = React.useState(ESTATE_BROKERS[0].id);
  const [brokerName, setBrokerName] = React.useState(ESTATE_BROKERS[0].name);
  const [brokerPhone, setBrokerPhone] = React.useState(ESTATE_BROKERS[0].phone);
  const [brokerEmail, setBrokerEmail] = React.useState(ESTATE_BROKERS[0].email);
  
  // Zoom & Pan states for property image
  const [imageZoom, setImageZoom] = React.useState(100);
  const [imagePanX, setImagePanX] = React.useState(50);
  const [imagePanY, setImagePanY] = React.useState(50);

  // Property Reordering helpers
  const handleMoveProperty = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= apartments.length || fromIndex === toIndex) return;
    const reordered = [...apartments];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    const updated = reordered.map((p, idx) => ({ ...p, order: idx + 1 }));
    if (onReorderProperties) {
      onReorderProperties(updated);
    }
  };

  const handleMovePropertyUp = (index: number) => {
    handleMoveProperty(index, index - 1);
  };

  const handleMovePropertyDown = (index: number) => {
    handleMoveProperty(index, index + 1);
  };

  const handleSetPropertyOrder = (fromIndex: number, newPositionOneBased: number) => {
    const toIndex = Math.max(0, Math.min(apartments.length - 1, newPositionOneBased - 1));
    handleMoveProperty(fromIndex, toIndex);
  };

  // Agent Form states
  const [isAgentFormOpen, setIsAgentFormOpen] = React.useState(false);
  const [editingAgentId, setEditingAgentId] = React.useState<string | null>(null);
  const [agentName, setAgentName] = React.useState('');
  const [agentRole, setAgentRole] = React.useState('');
  const [agentCategory, setAgentCategory] = React.useState<'sales' | 'rentals' | 'office'>('sales');
  const [agentPhone, setAgentPhone] = React.useState('');
  const [agentEmail, setAgentEmail] = React.useState('');
  const [agentImage, setAgentImage] = React.useState('');
  const [agentOrder, setAgentOrder] = React.useState<number>(1);

  // Drag and drop states for team reordering
  const [draggedAgentIndex, setDraggedAgentIndex] = React.useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = React.useState<number | null>(null);

  // Zoom & Pan states for agent image
  const [agentZoom, setAgentZoom] = React.useState(100);
  const [agentPanX, setAgentPanX] = React.useState(50);
  const [agentPanY, setAgentPanY] = React.useState(50);
  const [agentFit, setAgentFit] = React.useState<'cover' | 'contain'>('cover');

  // Reordering helpers
  const handleMoveAgent = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= agents.length || fromIndex === toIndex) return;
    const reordered = [...agents];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    const updated = reordered.map((a, idx) => ({ ...a, order: idx + 1 }));
    if (onReorderAgents) {
      onReorderAgents(updated);
    }
  };

  const handleMoveAgentUp = (index: number) => {
    handleMoveAgent(index, index - 1);
  };

  const handleMoveAgentDown = (index: number) => {
    handleMoveAgent(index, index + 1);
  };

  const handleMoveAgentToTop = (index: number) => {
    handleMoveAgent(index, 0);
  };

  const handleMoveAgentToBottom = (index: number) => {
    handleMoveAgent(index, agents.length - 1);
  };

  // Upload file reader - sets preview, and also suggests/supports /public/ image filenames
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, isAgent: boolean) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        if (isAgent) {
          setAgentImage(reader.result);
        } else {
          setImageUrl(reader.result);
        }
      }
    };
    reader.readAsDataURL(file);
  };

  const handleOpenAddAgentForm = () => {
    setEditingAgentId(null);
    setAgentName('');
    setAgentRole('Брокер продажби');
    setAgentCategory('sales');
    setAgentPhone('');
    setAgentEmail('');
    setAgentImage('');
    setAgentOrder(agents.length + 1);
    setAgentZoom(100);
    setAgentPanX(50);
    setAgentPanY(50);
    setAgentFit('cover');
    setIsAgentFormOpen(true);
  };

  const handleOpenEditAgentForm = (agent: Agent) => {
    setEditingAgentId(agent.id);
    setAgentName(agent.name);
    setAgentRole(agent.role);
    setAgentCategory(agent.category);
    setAgentPhone(agent.phone);
    setAgentEmail(agent.email);
    setAgentImage(agent.image);
    const currIndex = agents.findIndex(a => a.id === agent.id);
    setAgentOrder(agent.order !== undefined && agent.order > 0 ? agent.order : (currIndex !== -1 ? currIndex + 1 : agents.length + 1));
    setAgentZoom(agent.imageZoom || 100);
    setAgentPanX(agent.imagePanX !== undefined ? agent.imagePanX : 50);
    setAgentPanY(agent.imagePanY !== undefined ? agent.imagePanY : 50);
    setAgentFit(agent.imageFit || 'cover');
    setIsAgentFormOpen(true);
  };

  const handleAgentFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!agentName || !agentRole || !agentPhone || !agentEmail) {
      alert('Моля, попълнете всички задължителни полета за служителя.');
      return;
    }

    const agentData: Agent = {
      id: editingAgentId || Date.now().toString(),
      name: agentName,
      role: agentRole,
      category: agentCategory,
      phone: agentPhone,
      email: agentEmail,
      order: Math.max(1, agentOrder || 1),
      image: agentImage.trim() || DEFAULT_AGENT_AVATAR,
      imageZoom: agentZoom,
      imagePanX: agentPanX,
      imagePanY: agentPanY,
      imageFit: agentFit
    };

    if (editingAgentId) {
      onEditAgent(agentData);
    } else {
      onAddAgent(agentData);
    }

    setIsAgentFormOpen(false);
    setEditingAgentId(null);
  };

  const handleOpenAddForm = () => {
    setEditingId(null);
    setTitle('');
    setType('sale');
    setEstateType('2-стаен');
    setPrice('125000');
    setArea('75');
    setRooms('2');
    setBathrooms('1');
    setFloor('4/8');
    setNeighborhood('Бриз');
    setImageUrl(STOCK_GALLERY_IMAGES[0]);
    setImageZoom(100);
    setImagePanX(50);
    setImagePanY(50);
    setDescription('Предлагаме Ви луксозен имот на перфектна локация, разполагащ с модерни удобства, отлични общи части и напълно готов за сделка.');
    setTagsInput('НОВА ОФЕРТА');
    setBrokerId(ESTATE_BROKERS[0].id);
    setBrokerName(ESTATE_BROKERS[0].name);
    setBrokerPhone(ESTATE_BROKERS[0].phone);
    setBrokerEmail(ESTATE_BROKERS[0].email);
    setIsFormOpen(true);
  };

  const handleOpenEditForm = (apartment: Apartment) => {
    setEditingId(apartment.id);
    const safeTitle = typeof apartment.title === 'object' ? (apartment.title as any)?.name || (apartment.title as any)?.title || '' : String(apartment.title || '');
    const safeNeighborhood = typeof apartment.neighborhood === 'object' ? (apartment.neighborhood as any)?.name || (apartment.neighborhood as any)?.label || '' : String(apartment.neighborhood || 'Бриз');
    const safeFloor = typeof apartment.floor === 'object' ? (apartment.floor as any)?.name || '1' : String(apartment.floor || '3/6');

    setTitle(safeTitle);
    setType(apartment.type === 'rent' ? 'rent' : 'sale');

    const estLower = String(apartment.estateType || '').toLowerCase();
    const isAptPlot = estLower.includes('парцел') || estLower.includes('тере') || estLower.includes('упи') || estLower.includes('земедел') || safeTitle.toLowerCase().includes('парцел') || safeTitle.toLowerCase().includes('упи') || safeTitle.toLowerCase().includes('терен');
    const isAptGarage = !isAptPlot && (estLower.includes('гараж') || estLower.includes('паркомяст') || safeTitle.toLowerCase().includes('гараж'));

    setEstateType(apartment.estateType || (isAptPlot ? 'Парцел' : isAptGarage ? 'Гараж' : (apartment.rooms ? `${apartment.rooms}-стаен` : '2-стаен')));
    setPrice(apartment.price !== undefined && apartment.price !== null ? String(apartment.price) : '0');
    setArea(apartment.area !== undefined && apartment.area !== null ? String(apartment.area) : '0');
    setRooms(apartment.rooms !== undefined && apartment.rooms !== null ? String(apartment.rooms) : '2');
    setBathrooms(apartment.bathrooms !== undefined && apartment.bathrooms !== null ? String(apartment.bathrooms) : '1');
    setFloor(safeFloor);
    setNeighborhood(safeNeighborhood);
    setImageUrl(apartment.image || STOCK_GALLERY_IMAGES[0]);
    setImageZoom(apartment.imageZoom || 100);
    setImagePanX(apartment.imagePanX !== undefined ? apartment.imagePanX : 50);
    setImagePanY(apartment.imagePanY !== undefined ? apartment.imagePanY : 50);
    setDescription(apartment.description || '');
    setTagsInput(Array.isArray(apartment.tags) ? apartment.tags.filter(t => !String(t).toUpperCase().includes('АКТ 16')).join(', ') : 'НОВА ОФЕРТА');
    
    const matched = findBrokerByNameOrId(apartment.brokerName || apartment.phone);
    setBrokerId(matched?.id || ESTATE_BROKERS[0].id);
    setBrokerName(apartment.brokerName || matched?.name || ESTATE_BROKERS[0].name);
    setBrokerPhone(apartment.brokerPhone || apartment.phone || matched?.phone || ESTATE_BROKERS[0].phone);
    setBrokerEmail(apartment.brokerEmail || matched?.email || ESTATE_BROKERS[0].email);
    setIsFormOpen(true);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!title || !price || !area || !description) {
      return;
    }

    const priceNum = parseFloat(price);
    const areaNum = parseFloat(area);
    const isPlot = estateType.toLowerCase().includes('парцел') || estateType.toLowerCase().includes('тере') || estateType.toLowerCase().includes('упи') || estateType.toLowerCase().includes('земедел') || estateType.toLowerCase().includes('нива');
    const isGarage = !isPlot && (estateType.toLowerCase().includes('гараж') || estateType.toLowerCase().includes('паркомяст'));
    const roomsNum = (isPlot || isGarage) ? 0 : (parseInt(rooms, 10) >= 0 ? parseInt(rooms, 10) : 2);
    const bathroomsNum = isPlot ? 0 : (parseInt(bathrooms, 10) || 1);

    const parsedTags = tagsInput
      .split(',')
      .map(t => t.trim())
      .filter(t => t.length > 0 && !t.toUpperCase().includes('АКТ 16') && !t.toUpperCase().includes('АКТ16'));

    const existing = editingId ? apartments.find(a => 
      String(a.id) === String(editingId) || 
      (a.estateId && String(a.estateId) === String(editingId)) ||
      (a.code && String(a.code) === String(editingId)) ||
      (String(a.id).replace(/^ea-/, '') === String(editingId).replace(/^ea-/, ''))
    ) : undefined;

    const propertyId = editingId || (Date.now() % 1000000).toString();

    const propertyData: Apartment = {
      ...(existing || {}),
      id: propertyId,
      estateId: existing?.estateId || propertyId.replace(/^ea-/, ''),
      title: title.trim(),
      type,
      estateType,
      price: priceNum,
      area: areaNum,
      rooms: roomsNum,
      bathrooms: bathroomsNum,
      floor: isPlot ? 'Терен' : floor,
      neighborhood: neighborhood.trim(),
      image: imageUrl.trim(),
      tags: parsedTags,
      description: description.trim(),
      phone: brokerPhone || existing?.phone || AGENCY_CONFIG.phone,
      brokerName: brokerName || 'Варна Имотен Център',
      brokerPhone: brokerPhone || AGENCY_CONFIG.phone,
      brokerEmail: brokerEmail || AGENCY_CONFIG.email,
      isCustom: true,
      isModifiedByAdmin: true,
      isActive: true,
      imageZoom,
      imagePanX,
      imagePanY
    };

    if (editingId) {
      onEditProperty(propertyData);
    } else {
      onAddProperty(propertyData);
    }

    setIsFormOpen(false);
    setEditingId(null);
  };

  return (
    <section className="bg-slate-50 min-h-screen py-8 md:py-10 font-sans">
      <div className="max-w-7xl mx-auto px-4">
        {/* Title panel */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3">
              {onExitAdmin && (
                <button
                  id="admin-exit-btn"
                  onClick={onExitAdmin}
                  className="px-3 py-1.5 bg-white border border-slate-200 text-slate-700 hover:text-[#0F1E32] hover:bg-slate-100 font-bold rounded-lg text-xs tracking-wider uppercase transition flex items-center gap-1.5 focus:outline-none shadow-sm cursor-pointer"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Към сайта
                </button>
              )}
              <h1 className="text-2xl md:text-3xl font-black text-[#0F1E32] tracking-tight flex items-center gap-2">
                Административен панел
              </h1>
            </div>
            <p className="text-sm text-slate-500 mt-1">
              {activeAdminTab === 'properties' 
                ? 'Преглед и управление на активните оферти, синхронизирани директно от Estate Assistant API.'
                : activeAdminTab === 'agents'
                ? 'Тук можете да променяте данните на Вашите служители, техните снимки, роли, телефонни номера и контакти.'
                : activeAdminTab === 'images'
                ? 'Преглед и управление на файловете в public/, търсене и бързо копиране на адреси, както и регулиране на изгледа и фокуса на снимките.'
                : activeAdminTab === 'content'
                ? 'Пълен визуален CMS редактор: редактирайте заглавия, бутони, текстове, телефони, снимки, шрифтове, популярни локации и футър.'
                : 'Интеграция и автоматична синхронизация с платформата Estate Assistant (REST API, Webhook synch.php и Cron).'
              }
            </p>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap">
            {user && (
              <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl border border-slate-200 shadow-sm text-xs text-slate-700">
                <div className="w-6 h-6 rounded-full bg-amber-100 border border-amber-300 flex items-center justify-center text-amber-800 font-bold text-[11px]">
                  {user.email ? user.email.charAt(0).toUpperCase() : 'A'}
                </div>
                <div className="hidden sm:block">
                  <p className="font-bold text-slate-900 leading-tight">{user.displayName || 'Администратор'}</p>
                  <p className="text-[10px] text-slate-500 truncate max-w-[140px]">{user.email}</p>
                </div>
                <button
                  type="button"
                  onClick={() => signOut()}
                  title="Изход от администраторския профил"
                  className="ml-1 p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition cursor-pointer"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            )}

            {activeAdminTab === 'properties' ? (
              onRefreshProperties && (
                <button
                  id="admin-refresh-properties-btn"
                  onClick={onRefreshProperties}
                  className="px-4 py-2.5 bg-[#0F1E32] hover:bg-slate-800 text-white font-bold rounded-lg text-xs tracking-wider uppercase shadow transition flex items-center gap-1.5 focus:outline-none cursor-pointer"
                >
                  <RefreshCw className="w-4 h-4 text-[#C5A46E]" />
                  Обнови списъка
                </button>
              )
            ) : activeAdminTab === 'agents' ? (
              <button
                id="admin-add-agent-btn"
                onClick={handleOpenAddAgentForm}
                className="px-5 py-2.5 bg-[#C5A46E] hover:bg-[#B1905E] text-white font-bold rounded-lg text-xs tracking-wider uppercase shadow-md transition flex items-center justify-center focus:outline-none cursor-pointer"
              >
                ДОБАВИ СЛУЖИТЕЛ
              </button>
            ) : null}
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex border-b border-slate-200 mb-6 overflow-x-auto">
          <button
            id="admin-tab-properties"
            onClick={() => setActiveAdminTab('properties')}
            className={`py-3 px-5 text-xs md:text-sm font-bold tracking-wider uppercase border-b-2 transition-all flex items-center gap-2 focus:outline-none shrink-0 ${
              activeAdminTab === 'properties'
                ? 'border-[#C5A46E] text-[#0F1E32]'
                : 'border-transparent text-slate-500 hover:text-[#0F1E32]'
            }`}
          >
            <Home className="w-4 h-4" />
            Управление на Имоти
          </button>
          <button
            id="admin-tab-agents"
            onClick={() => setActiveAdminTab('agents')}
            className={`py-3 px-5 text-xs md:text-sm font-bold tracking-wider uppercase border-b-2 transition-all flex items-center gap-2 focus:outline-none shrink-0 ${
              activeAdminTab === 'agents'
                ? 'border-[#C5A46E] text-[#0F1E32]'
                : 'border-transparent text-slate-500 hover:text-[#0F1E32]'
            }`}
          >
            <Users className="w-4 h-4" />
            Управление на Екип
          </button>
          <button
            id="admin-tab-images"
            onClick={() => setActiveAdminTab('images')}
            className={`py-3 px-5 text-xs md:text-sm font-bold tracking-wider uppercase border-b-2 transition-all flex items-center gap-2 focus:outline-none shrink-0 ${
              activeAdminTab === 'images'
                ? 'border-[#C5A46E] text-[#0F1E32]'
                : 'border-transparent text-slate-500 hover:text-[#0F1E32]'
            }`}
          >
            <HardDrive className="w-4 h-4 text-[#C5A46E]" />
            Медия & Файлове (public)
          </button>
          <button
            id="admin-tab-content"
            onClick={() => setActiveAdminTab('content')}
            className={`py-3 px-5 text-xs md:text-sm font-bold tracking-wider uppercase border-b-2 transition-all flex items-center gap-2 focus:outline-none shrink-0 ${
              activeAdminTab === 'content'
                ? 'border-[#C5A46E] text-[#0F1E32]'
                : 'border-transparent text-slate-500 hover:text-[#0F1E32]'
            }`}
          >
            <Palette className="w-4 h-4 text-[#C5A46E]" />
            Редактор на сайта (CMS)
          </button>
          <button
            id="admin-tab-seo"
            onClick={() => setActiveAdminTab('seo')}
            className={`py-3 px-5 text-xs md:text-sm font-bold tracking-wider uppercase border-b-2 transition-all flex items-center gap-2 focus:outline-none shrink-0 ${
              activeAdminTab === 'seo'
                ? 'border-[#C5A46E] text-[#0F1E32]'
                : 'border-transparent text-slate-500 hover:text-[#0F1E32]'
            }`}
          >
            <Search className="w-4 h-4 text-[#C5A46E]" />
            SEO Оптимизация
          </button>
          <button
            id="admin-tab-inquiries"
            onClick={() => setActiveAdminTab('inquiries')}
            className={`py-3 px-5 text-xs md:text-sm font-bold tracking-wider uppercase border-b-2 transition-all flex items-center gap-2 focus:outline-none shrink-0 ${
              activeAdminTab === 'inquiries'
                ? 'border-[#C5A46E] text-[#0F1E32]'
                : 'border-transparent text-slate-500 hover:text-[#0F1E32]'
            }`}
          >
            <Mail className="w-4 h-4 text-[#C5A46E]" />
            Запитвания & Огледи
          </button>
          <button
            id="admin-tab-sync"
            onClick={() => setActiveAdminTab('sync')}
            className={`py-3 px-5 text-xs md:text-sm font-bold tracking-wider uppercase border-b-2 transition-all flex items-center gap-2 focus:outline-none shrink-0 ${
              activeAdminTab === 'sync'
                ? 'border-[#C5A46E] text-[#0F1E32]'
                : 'border-transparent text-slate-500 hover:text-[#0F1E32]'
            }`}
          >
            <RefreshCw className="w-4 h-4 text-[#C5A46E]" />
            API Синхронизация
          </button>
          <button
            id="admin-tab-firebase"
            onClick={() => setActiveAdminTab('firebase')}
            className={`py-3 px-5 text-xs md:text-sm font-bold tracking-wider uppercase border-b-2 transition-all flex items-center gap-2 focus:outline-none shrink-0 ${
              activeAdminTab === 'firebase'
                ? 'border-[#C5A46E] text-[#0F1E32]'
                : 'border-transparent text-slate-500 hover:text-[#0F1E32]'
            }`}
          >
            <Database className="w-4 h-4 text-[#C5A46E]" />
            Облачна база данни
          </button>
        </div>

        {/* Form Modal (Add/Edit) */}
        {isFormOpen && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-[100] p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl max-w-2xl w-full min-h-[500px] overflow-hidden shadow-2xl relative font-sans max-h-[90vh] flex flex-col my-auto">
              {/* Header */}
              <div className="bg-[#0F1E32] text-white py-4 px-6 flex justify-between items-center shrink-0">
                <span className="font-bold text-sm tracking-widest uppercase text-[#C5A46E]">
                  {editingId ? 'Редактиране на имот 🛠️' : 'Качване на нов имот ✨'}
                </span>
                <button
                  id="admin-form-close"
                  onClick={() => setIsFormOpen(false)}
                  className="text-gray-400 hover:text-white font-extrabold text-sm focus:outline-none"
                >
                  Затвори ✕
                </button>
              </div>

              {/* Form Content */}
              <form onSubmit={handleFormSubmit} className="p-6 md:p-8 space-y-4 overflow-y-auto flex-grow text-xs text-slate-700">
                
                {/* Title */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Име на офертата *</label>
                  <input
                    id="property-title"
                    type="text"
                    required
                    placeholder="напр. 2-стаен - Варна, Бриз"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 text-sm focus:ring-1 focus:ring-[#C5A46E] focus:outline-none"
                  />
                </div>

                {/* Offer Type & Neighborhood */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Тип сделка *</label>
                    <div className="flex gap-2.5 mt-1">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="type"
                          checked={type === 'sale'}
                          onChange={() => setType('sale')}
                          className="accent-[#C5A46E]"
                        />
                        <span>За продажба (Продава)</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="type"
                          checked={type === 'rent'}
                          onChange={() => setType('rent')}
                          className="accent-[#1D608A]"
                        />
                        <span>Под наем (Наем)</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">Квартал *</label>
                      <button
                        type="button"
                        onClick={() => setIsCustomNeighborhood(!isCustomNeighborhood)}
                        className="text-[10px] text-[#007BB5] hover:underline font-semibold cursor-pointer"
                      >
                        {isCustomNeighborhood ? '📋 От списъка' : '✏️ Друг квартал'}
                      </button>
                    </div>
                    {isCustomNeighborhood ? (
                      <input
                        type="text"
                        value={neighborhood}
                        onChange={(e) => setNeighborhood(e.target.value)}
                        placeholder="Напишете име на квартал..."
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-2.5 text-xs focus:ring-1 focus:ring-[#C5A46E] focus:outline-none"
                      />
                    ) : (
                      <select
                        id="property-neighborhood"
                        value={neighborhood}
                        onChange={(e) => setNeighborhood(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-2 text-xs focus:ring-1 focus:ring-[#C5A46E] focus:outline-none cursor-pointer"
                      >
                        {neighborhood && !neighborhoods.includes(neighborhood) && (
                          <option value={neighborhood}>{neighborhood} (текущ)</option>
                        )}
                        {neighborhoods.map((n, idx) => {
                          const val = typeof n === 'object' ? (n as any)?.name || (n as any)?.label || '' : String(n || '');
                          if (!val) return null;
                          return <option key={idx} value={val}>{val}</option>;
                        })}
                      </select>
                    )}
                  </div>
                </div>

                {/* Price & Area */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                      Цена (€ EUR) *
                    </label>
                    <input
                      id="property-price"
                      type="number"
                      required
                      placeholder="напр. 135000 или 450"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 text-sm focus:ring-1 focus:ring-[#C5A46E] focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Площ (кв.м. m²) *</label>
                    <input
                      id="property-area"
                      type="number"
                      required
                      placeholder="напр. 68"
                      value={area}
                      onChange={(e) => setArea(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 text-sm focus:ring-1 focus:ring-[#C5A46E] focus:outline-none"
                    />
                  </div>
                </div>

                {/* Estate Type & Rooms */}
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Вид имот / Категория *</label>
                    <select
                      id="property-estate-type"
                      value={estateType}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEstateType(val);
                        const cat = ESTATE_ASSISTANT_CATEGORIES.find(c => c.name.toLowerCase() === val.toLowerCase());
                        if (cat) {
                          if (cat.defaultRooms !== undefined) setRooms(String(cat.defaultRooms));
                          if (cat.defaultFloor) setFloor(cat.defaultFloor);
                        } else {
                          if (val.includes('1-стаен')) setRooms('1');
                          else if (val.includes('2-стаен')) setRooms('2');
                          else if (val.includes('3-стаен')) setRooms('3');
                          else if (val.includes('4-стаен') || val.includes('многостаен')) setRooms('4');
                        }
                      }}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-2 text-xs focus:ring-1 focus:ring-[#C5A46E] focus:outline-none font-semibold text-[#0F1E32]"
                    >
                      {estateType && !ESTATE_ASSISTANT_CATEGORIES.some(c => c.name.toLowerCase() === estateType.toLowerCase()) && (
                        <option value={estateType}>{estateType}</option>
                      )}
                      {ESTATE_ASSISTANT_CATEGORIES.map(cat => (
                        <option key={cat.id} value={cat.name}>{cat.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Стаи</label>
                    <select
                      id="property-rooms"
                      value={rooms}
                      onChange={(e) => setRooms(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-2 text-xs focus:outline-none"
                    >
                      <option value="0">0 (Парцел / Гараж)</option>
                      <option value="1">1 (едностаен)</option>
                      <option value="2">2 (двустаен)</option>
                      <option value="3">3 (тристаен)</option>
                      <option value="4">4+ (многостаен)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Етаж *</label>
                    <input
                      id="property-floor"
                      type="text"
                      required
                      placeholder="напр. 3/6, Партер или Терен"
                      value={floor}
                      onChange={(e) => setFloor(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 text-xs focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Санитарни възли</label>
                    <select
                      id="property-bathrooms"
                      value={bathrooms}
                      onChange={(e) => setBathrooms(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-2 text-xs focus:outline-none"
                    >
                      <option value="0">0 (Парцел / Гараж)</option>
                      <option value="1">1 баня</option>
                      <option value="2">2 бани</option>
                      <option value="3">3+ бани</option>
                    </select>
                  </div>
                </div>

                {/* Image Selection Block with Previews */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                    Снимка на имота *
                  </label>
                  
                  {/* File uploader box */}
                  <div className="mb-3 border-2 border-dashed border-slate-200 hover:border-[#C5A46E] rounded-xl p-4 text-center cursor-pointer bg-slate-50 hover:bg-slate-50/50 transition-colors relative overflow-hidden">
                    <input
                      type="file"
                      accept="image/*,video/*"
                      onChange={(e) => handleFileUpload(e, false)}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="flex flex-col items-center justify-center gap-1">
                      <div className="flex items-center gap-2 text-xl">
                        <span>📸</span>
                        <span>🎥</span>
                      </div>
                      <span className="text-xs font-bold text-[#0F1E32]">Качете снимка или видео от компютъра</span>
                      <span className="text-[10px] text-slate-400">Моментален предварителен преглед в сайта</span>
                    </div>
                  </div>

                  <p className="text-[10px] text-gray-500 mb-2">Или изберете луксозна професионална снимка от галерията ни с един клик:</p>
                  
                  {/* Photo Thumbnails Choice */}
                  <div className="grid grid-cols-5 gap-2 mb-3 bg-slate-50 p-2 rounded-xl border border-slate-100 max-h-[110px] overflow-y-auto">
                    {STOCK_GALLERY_IMAGES.map((img, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setImageUrl(img)}
                        className={`relative aspect-video rounded overflow-hidden border-2 transition-all ${
                          imageUrl === img ? 'border-[#C5A46E] ring-1 ring-[#C5A46E]' : 'border-transparent hover:border-slate-300'
                        }`}
                      >
                        <img 
                          src={img} 
                          alt="Gallery item"
                          referrerPolicy="no-referrer"
                          className="w-full h-full object-cover" 
                        />
                      </button>
                    ))}
                  </div>

                  {/* Manual URL or public/ path input */}
                  <div className="flex flex-col sm:flex-row gap-2 mb-1">
                    <div className="flex flex-grow">
                      <span className="bg-slate-100 border border-slate-200 border-r-0 rounded-l-lg py-2 px-3 text-slate-400 select-none flex items-center justify-center">
                        <ImageIcon className="w-4 h-4" />
                      </span>
                      <input
                        id="property-image"
                        type="text"
                        placeholder="Път в public (напр. /hero.jpg, /imot1.jpg, /video1.mp4) или интернет адрес..."
                        value={imageUrl}
                        onChange={(e) => setImageUrl(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-r-lg py-2 px-3 text-xs focus:outline-none font-mono"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setMediaPicker({
                        isOpen: true,
                        allowedType: 'all',
                        title: 'Изберете снимка или видео от public/ за имота',
                        onSelect: (url) => setImageUrl(url)
                      })}
                      className="px-3.5 py-2 bg-[#0F1E32] hover:bg-[#C5A46E] text-white rounded-lg text-xs font-bold transition flex items-center justify-center gap-1.5 shrink-0 shadow-sm cursor-pointer"
                      title="Отвори медийната библиотека от качените файлове в public"
                    >
                      <HardDrive className="w-3.5 h-3.5 text-[#C5A46E]" />
                      <span>📁 Избери от public</span>
                    </button>
                  </div>
                  <p className="text-[10px] text-slate-400 mb-3">
                    💡 <strong>Автоматично от public/:</strong> Натиснете бутона <strong>„📁 Избери от public“</strong> за да изберете автоматично качените снимки или видеа от папката.
                  </p>

                  {/* Interactive zoom and position sliders */}
                  <div className="mt-4 bg-slate-50 border border-slate-100 p-4 rounded-xl space-y-3">
                    <span className="text-xs font-bold text-[#0F1E32] block uppercase tracking-wider">
                      📐 Преглед и центриране на снимката
                    </span>
                    <p className="text-[10px] text-slate-400">Използвайте плъзгачите по-долу, за да напаснете снимката (зуум и местене) в контейнера за перфектен вид на сайта.</p>
                    
                    {/* Live Crop Preview Box */}
                    <div className="flex flex-col items-center justify-center">
                      <div className="w-full max-w-[280px] aspect-video bg-slate-200 rounded-lg overflow-hidden border border-slate-300 relative shadow-inner">
                        <img
                          src={imageUrl}
                          alt="Preview crop"
                          className="w-full h-full object-cover transition-all duration-75"
                          style={{
                            transform: `scale(${imageZoom / 100})`,
                            objectPosition: `${imagePanX}% ${imagePanY}%`,
                            transformOrigin: `${imagePanX}% ${imagePanY}%`
                          }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=100&q=80';
                          }}
                        />
                      </div>
                      {imageUrl && (
                        <button
                          type="button"
                          onClick={() => {
                            setImageUrl('https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800&fit=crop&q=60');
                            setImageZoom(100);
                            setImagePanX(50);
                            setImagePanY(50);
                          }}
                          className="mt-2.5 px-3 py-1 bg-red-50 hover:bg-red-600 hover:text-white text-red-600 rounded text-[10px] font-bold uppercase transition flex items-center gap-1 focus:outline-none"
                        >
                          <Trash2 className="w-3 h-3" />
                          Изтрий / Премахни снимката
                        </button>
                      )}
                    </div>

                    <div className="space-y-3 text-xs font-medium text-slate-600">
                      {/* Zoom slider */}
                      <div>
                        <div className="flex justify-between items-center text-[10px] mb-1.5 text-slate-500 uppercase tracking-wider font-semibold">
                          <span>Мащаб (Зуум):</span>
                          <span className="font-bold text-[#C5A46E]">
                            {imageZoom}% {imageZoom < 100 ? '(Отдалечено)' : imageZoom > 100 ? '(Приближено)' : '(Нормален)'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setImageZoom(prev => Math.max(30, prev - 5))}
                            className="w-7 h-7 bg-slate-100 hover:bg-slate-200 active:scale-95 rounded font-bold text-slate-700 flex items-center justify-center text-sm transition"
                            title="Отдалечи (-5%)"
                          >
                            -
                          </button>
                          <input
                            type="range"
                            min="30"
                            max="300"
                            step="1"
                            value={imageZoom}
                            onChange={(e) => setImageZoom(parseInt(e.target.value))}
                            className="w-full accent-[#C5A46E] cursor-pointer"
                          />
                          <button
                            type="button"
                            onClick={() => setImageZoom(prev => Math.min(300, prev + 5))}
                            className="w-7 h-7 bg-slate-100 hover:bg-slate-200 active:scale-95 rounded font-bold text-slate-700 flex items-center justify-center text-sm transition"
                            title="Приближи (+5%)"
                          >
                            +
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          <button
                            type="button"
                            onClick={() => setImageZoom(75)}
                            className={`px-2 py-0.5 rounded text-[10px] font-semibold transition ${imageZoom === 75 ? 'bg-[#C5A46E] text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'}`}
                          >
                            🔍 75% Отдалечи
                          </button>
                          <button
                            type="button"
                            onClick={() => setImageZoom(100)}
                            className={`px-2 py-0.5 rounded text-[10px] font-semibold transition ${imageZoom === 100 ? 'bg-[#C5A46E] text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'}`}
                          >
                            100% Нормален
                          </button>
                          <button
                            type="button"
                            onClick={() => setImageZoom(125)}
                            className={`px-2 py-0.5 rounded text-[10px] font-semibold transition ${imageZoom === 125 ? 'bg-[#C5A46E] text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'}`}
                          >
                            🔍+ 125%
                          </button>
                        </div>
                      </div>

                      {/* Pan X slider */}
                      <div>
                        <div className="flex justify-between items-center text-[10px] mb-1.5 text-slate-500 uppercase tracking-wider font-semibold">
                          <span>Хоризонтално изместване (ляво / дясно):</span>
                          <span className="font-bold text-[#C5A46E]">{imagePanX}%</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={imagePanX}
                          onChange={(e) => setImagePanX(parseInt(e.target.value))}
                          className="w-full accent-[#C5A46E] cursor-pointer"
                        />
                      </div>

                      {/* Pan Y slider */}
                      <div>
                        <div className="flex justify-between text-[10px] mb-1 text-slate-500 uppercase tracking-wider font-semibold">
                          <span>Вертикално изместване (горе / долу):</span>
                          <span>{imagePanY}%</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={imagePanY}
                          onChange={(e) => setImagePanY(parseInt(e.target.value))}
                          className="w-full accent-[#C5A46E] cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status Badges e.g., NOVI, EKSTRA */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Рекламни етикети (Разделени със запетая)</label>
                  <input
                    id="property-tags"
                    type="text"
                    placeholder="напр. ТОП ОФЕРТА, МОРСКА ГЛЕДКА, ЛУКС"
                    value={tagsInput}
                    onChange={(e) => setTagsInput(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 text-xs focus:outline-none"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Подробно описание *</label>
                  <textarea
                    id="property-description"
                    rows={3}
                    required
                    placeholder="Напишете детайли за апартамента, екстрите на сградата, локацията и гледката..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 text-xs focus:outline-none"
                  />
                </div>

                {/* Buttons block */}
                <div className="flex justify-end gap-2 pt-4">
                  <button
                    id="btn-form-cancel"
                    type="button"
                    onClick={() => setIsFormOpen(false)}
                    className="px-4 py-2 text-slate-600 hover:text-[#0F1E32] font-semibold text-xs focus:outline-none"
                  >
                    Отказ
                  </button>
                  <button
                    id="btn-form-save"
                    type="submit"
                    className="px-6 py-2 bg-[#0F1E32] hover:bg-[#C5A46E] text-white font-bold rounded-lg text-xs uppercase tracking-widest transition"
                  >
                    Запиши офертата ✅
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Agent Form Modal (Add/Edit) */}
        {isAgentFormOpen && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-[100] p-3 sm:p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl max-w-5xl lg:max-w-6xl w-full min-h-[450px] overflow-hidden shadow-2xl relative font-sans max-h-[92vh] flex flex-col my-auto">
              {/* Header */}
              <div className="bg-[#0F1E32] text-white py-3.5 px-6 flex justify-between items-center shrink-0">
                <div className="flex items-center gap-2.5">
                  <span className="font-bold text-sm tracking-widest uppercase text-[#C5A46E]">
                    {editingAgentId ? 'Редактиране на служител 🛠️' : 'Добавяне на нов служител ✨'}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-[#C5A46E]/20 text-[#C5A46E] border border-[#C5A46E]/40">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    1:1 Преглед в сайта
                  </span>
                </div>
                <button
                  id="admin-agent-form-close"
                  onClick={() => setIsAgentFormOpen(false)}
                  className="text-gray-400 hover:text-white font-extrabold text-sm focus:outline-none cursor-pointer"
                >
                  Затвори ✕
                </button>
              </div>

              {/* Form Content in 2-Column Grid */}
              <form onSubmit={handleAgentFormSubmit} className="flex-grow overflow-y-auto flex flex-col justify-between">
                <div className="p-4 sm:p-6 md:p-7 grid grid-cols-1 md:grid-cols-12 gap-6 text-xs text-slate-700">
                  
                  {/* Left Column: Form Fields & Photo Controls (col-span-7) */}
                  <div className="md:col-span-7 space-y-4">
                    {/* Name */}
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 font-sans">
                        Три имена *
                      </label>
                      <input
                        id="agent-form-name"
                        type="text"
                        required
                        autoFocus
                        placeholder="напр. Мария Петрова"
                        value={agentName}
                        onChange={(e) => setAgentName(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 text-sm focus:ring-1 focus:ring-[#C5A46E] focus:outline-none font-sans"
                      />
                    </div>

                    {/* Role / Position */}
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 font-sans">
                        Позиция в компанията *
                      </label>
                      <input
                        id="agent-form-role"
                        type="text"
                        required
                        placeholder="напр. Брокер продажби / Мениджър наеми"
                        value={agentRole}
                        onChange={(e) => setAgentRole(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 text-sm focus:ring-1 focus:ring-[#C5A46E] focus:outline-none font-sans"
                      />
                    </div>

                    {/* Category & Phone */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 font-sans">
                          Категория *
                        </label>
                        <select
                          id="agent-form-category"
                          value={agentCategory}
                          onChange={(e) => setAgentCategory(e.target.value as any)}
                          className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 text-xs focus:outline-none font-sans"
                        >
                          <option value="sales">Продажби (Sales)</option>
                          <option value="rentals">Наеми (Rentals)</option>
                          <option value="office">Офис / Администрация</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 font-sans">
                          Телефон *
                        </label>
                        <input
                          id="agent-form-phone"
                          type="text"
                          required
                          placeholder="напр. +359 878 859 385"
                          value={agentPhone}
                          onChange={(e) => setAgentPhone(e.target.value)}
                          className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 text-xs focus:outline-none font-sans"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 font-sans">
                        Работен Имейл *
                      </label>
                      <input
                        id="agent-form-email"
                        type="email"
                        required
                        placeholder="напр. agent@varna-imoti.bg"
                        value={agentEmail}
                        onChange={(e) => setAgentEmail(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 text-xs focus:outline-none font-sans"
                      />
                    </div>

                    {/* Order / Position in team */}
                    <div className="bg-amber-50/60 border border-amber-200/80 rounded-xl p-3.5 space-y-2 font-sans">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-bold uppercase tracking-wider text-[#0F1E32] flex items-center gap-1.5">
                          <ListOrdered className="w-4 h-4 text-[#C5A46E]" />
                          Поредност на показване в сайта
                        </label>
                        <span className="text-xs font-extrabold text-[#C5A46E] bg-white px-2.5 py-0.5 rounded-md border border-amber-200/70 shadow-2xs">
                          {agentOrder === 1 ? '🥇 1-ви в сайта' : `#${agentOrder} позиция`}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-600 leading-snug">
                        Определя редът, в който този служител ще се показва в публичната страница <strong>„Екип“</strong> (кой 1-ви, кой следващ, кой последен).
                      </p>
                      <div className="flex items-center gap-2 pt-1 flex-wrap">
                        <input
                          id="agent-form-order"
                          type="number"
                          min="1"
                          max={Math.max(1, agents.length + 1)}
                          value={agentOrder}
                          onChange={(e) => setAgentOrder(Math.max(1, parseInt(e.target.value) || 1))}
                          className="w-20 bg-white border border-slate-300 rounded-lg py-1.5 px-3 text-sm font-bold text-[#0F1E32] focus:ring-1 focus:ring-[#C5A46E] focus:outline-none"
                        />
                        <div className="flex flex-wrap gap-1.5">
                          <button
                            type="button"
                            onClick={() => setAgentOrder(1)}
                            className={`px-2.5 py-1 rounded-lg text-xs font-bold border transition cursor-pointer ${
                              agentOrder === 1 
                                ? 'bg-[#0F1E32] text-white border-[#0F1E32]' 
                                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
                            }`}
                          >
                            🥇 Направи 1-ви
                          </button>
                          {agents.length > 2 && (
                            <button
                              type="button"
                              onClick={() => setAgentOrder(Math.max(2, Math.ceil((agents.length + (editingAgentId ? 0 : 1)) / 2)))}
                              className="px-2.5 py-1 rounded-lg text-xs font-bold bg-white text-slate-600 border border-slate-200 hover:bg-slate-100 transition cursor-pointer"
                            >
                              📍 В средата
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setAgentOrder(editingAgentId ? agents.length : agents.length + 1)}
                            className={`px-2.5 py-1 rounded-lg text-xs font-bold border transition cursor-pointer ${
                              agentOrder === (editingAgentId ? agents.length : agents.length + 1)
                                ? 'bg-[#0F1E32] text-white border-[#0F1E32]' 
                                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
                            }`}
                          >
                            🏁 Последен (#{editingAgentId ? agents.length : agents.length + 1})
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Photo selection (Avatar) */}
                    <div className="space-y-2.5 pt-2 border-t border-slate-100 font-sans">
                      <div className="flex items-center justify-between">
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-600">
                          Снимка на служителя *
                        </label>
                        <span className="text-[11px] font-semibold text-[#C5A46E] flex items-center gap-1">
                          <span>👁️ На живо в 1:1 рамката</span>
                        </span>
                      </div>
                      
                      {/* Direct upload zone for agent */}
                      <div className="border-2 border-dashed border-slate-200 hover:border-[#C5A46E] rounded-xl p-3.5 text-center cursor-pointer bg-slate-50 hover:bg-slate-50/50 transition-colors relative overflow-hidden group">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileUpload(e, true)}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        <div className="flex flex-col items-center justify-center gap-0.5">
                          <span className="text-xl group-hover:scale-110 transition-transform">📸</span>
                          <span className="text-xs font-bold text-[#0F1E32]">Качете портрет директно от компютъра</span>
                          <span className="text-[10px] text-slate-400">Снимката се зарежда веднага в реалната 1:1 рамка вдясно</span>
                        </div>
                      </div>

                      {/* Manual URL or public/ path input */}
                      <div className="flex flex-col sm:flex-row gap-2">
                        <div className="flex flex-grow">
                          <span className="bg-slate-100 border border-slate-200 border-r-0 rounded-l-lg py-2 px-3 text-slate-400 select-none flex items-center justify-center">
                            <ImageIcon className="w-4 h-4" />
                          </span>
                          <input
                            id="agent-form-image-url"
                            type="text"
                            placeholder="Път към файл в public (напр. /agent1.jpg) или URL адрес..."
                            value={agentImage}
                            onChange={(e) => setAgentImage(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 rounded-r-lg py-2 px-3 text-xs focus:outline-none font-mono"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => setMediaPicker({
                            isOpen: true,
                            allowedType: 'image',
                            title: 'Изберете снимка от public/ за брокера',
                            onSelect: (url) => setAgentImage(url)
                          })}
                          className="px-3.5 py-2 bg-[#0F1E32] hover:bg-[#C5A46E] text-white rounded-lg text-xs font-bold transition flex items-center justify-center gap-1.5 shrink-0 shadow-sm cursor-pointer"
                          title="Отвори медийната библиотека от качествените файлове в public"
                        >
                          <HardDrive className="w-3.5 h-3.5 text-[#C5A46E]" />
                          <span>📁 Избери от public</span>
                        </button>
                      </div>

                      {agentImage && (
                        <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg p-2 text-[11px] text-emerald-800">
                          <span className="font-semibold flex items-center gap-1">
                            <span>✅</span> Снимката е заредена в 1:1 рамката вдясно
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setAgentImage('');
                              setAgentZoom(100);
                              setAgentPanX(50);
                              setAgentPanY(50);
                              setAgentFit('cover');
                            }}
                            className="text-red-500 hover:text-red-700 text-[10px] font-bold uppercase flex items-center gap-1 cursor-pointer"
                          >
                            <Trash2 className="w-3 h-3" /> Премахни
                          </button>
                        </div>
                      )}

                      {/* Photo Positioning & Framing Sliders */}
                      <div className="mt-3 bg-slate-50 border border-slate-200/80 p-4 rounded-xl space-y-3.5">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-extrabold text-[#0F1E32] uppercase tracking-wider flex items-center gap-1.5">
                            📐 Наместване в рамката
                          </span>
                          
                          {/* Mode Toggle: Cover vs Contain */}
                          <div className="flex items-center gap-1 bg-white p-0.5 rounded-lg border border-slate-200">
                            <button
                              type="button"
                              onClick={() => setAgentFit('cover')}
                              className={`px-2 py-0.5 rounded text-[10px] font-bold transition ${
                                agentFit === 'cover' ? 'bg-[#0F1E32] text-white' : 'text-slate-500 hover:text-slate-800'
                              }`}
                              title="Запълва цялата рамка на картата (портретен кадър)"
                            >
                              Запълване
                            </button>
                            <button
                              type="button"
                              onClick={() => setAgentFit('contain')}
                              className={`px-2 py-0.5 rounded text-[10px] font-bold transition ${
                                agentFit === 'contain' ? 'bg-[#C5A46E] text-white' : 'text-slate-500 hover:text-slate-800'
                              }`}
                              title="Побира 100% от оригиналния портрет без изрязване"
                            >
                              Цяла снимка (Fit)
                            </button>
                          </div>
                        </div>

                        {/* Zoom Slider */}
                        <div>
                          <div className="flex justify-between items-center text-[10px] mb-1 text-slate-600 uppercase tracking-wider font-bold">
                            <span>Мащаб (Зуум / Отдалечаване):</span>
                            <span className="font-mono font-bold text-[#C5A46E]">
                              {agentZoom}% {agentZoom < 100 ? '(Отдалечено 🔍-)' : agentZoom > 100 ? '(Приближено 🔍+)' : '(100% Нормален)'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setAgentZoom(prev => Math.max(50, prev - 5))}
                              className="w-7 h-7 bg-white hover:bg-slate-200 active:scale-95 rounded font-bold text-slate-700 border border-slate-200 flex items-center justify-center text-sm transition"
                              title="Отдалечи (-5%)"
                            >
                              -
                            </button>
                            <input
                              type="range"
                              min="50"
                              max="250"
                              step="1"
                              value={agentZoom}
                              onChange={(e) => setAgentZoom(parseInt(e.target.value))}
                              className="w-full accent-[#C5A46E] cursor-pointer"
                            />
                            <button
                              type="button"
                              onClick={() => setAgentZoom(prev => Math.min(250, prev + 5))}
                              className="w-7 h-7 bg-white hover:bg-slate-200 active:scale-95 rounded font-bold text-slate-700 border border-slate-200 flex items-center justify-center text-sm transition"
                              title="Приближи (+5%)"
                            >
                              +
                            </button>
                          </div>
                          {/* Quick presets for zoom */}
                          <div className="flex flex-wrap gap-1.5 mt-1.5">
                            <button
                              type="button"
                              onClick={() => { setAgentZoom(75); setAgentFit('contain'); }}
                              className={`px-2 py-0.5 rounded text-[10px] font-semibold transition border ${
                                agentZoom === 75 ? 'bg-[#C5A46E] text-white border-[#C5A46E]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
                              }`}
                            >
                              🔍- 75% Цяла снимка
                            </button>
                            <button
                              type="button"
                              onClick={() => { setAgentZoom(100); setAgentFit('cover'); }}
                              className={`px-2 py-0.5 rounded text-[10px] font-semibold transition border ${
                                agentZoom === 100 && agentFit === 'cover' ? 'bg-[#0F1E32] text-white border-[#0F1E32]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
                              }`}
                            >
                              100% Стандартен
                            </button>
                            <button
                              type="button"
                              onClick={() => { setAgentZoom(125); setAgentFit('cover'); }}
                              className={`px-2 py-0.5 rounded text-[10px] font-semibold transition border ${
                                agentZoom === 125 ? 'bg-[#C5A46E] text-white border-[#C5A46E]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
                              }`}
                            >
                              🔍+ 125% Приближи
                            </button>
                            <button
                              type="button"
                              onClick={() => { setAgentZoom(150); setAgentFit('cover'); }}
                              className={`px-2 py-0.5 rounded text-[10px] font-semibold transition border ${
                                agentZoom === 150 ? 'bg-[#C5A46E] text-white border-[#C5A46E]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
                              }`}
                            >
                              🔍+ 150% Портретен зуум
                            </button>
                          </div>
                        </div>

                        {/* Horizontal Pan X */}
                        <div>
                          <div className="flex justify-between items-center text-[10px] mb-1 text-slate-600 uppercase tracking-wider font-bold">
                            <span>Хоризонтално изместване (ляво / дясно):</span>
                            <span className="font-mono font-bold text-[#C5A46E]">{agentPanX}%</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setAgentPanX(prev => Math.max(0, prev - 5))}
                              className="w-7 h-7 bg-white hover:bg-slate-200 active:scale-95 rounded font-bold text-slate-700 border border-slate-200 flex items-center justify-center text-sm transition"
                              title="Наляво (-5%)"
                            >
                              ←
                            </button>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={agentPanX}
                              onChange={(e) => setAgentPanX(parseInt(e.target.value))}
                              className="w-full accent-[#C5A46E] cursor-pointer"
                            />
                            <button
                              type="button"
                              onClick={() => setAgentPanX(prev => Math.min(100, prev + 5))}
                              className="w-7 h-7 bg-white hover:bg-slate-200 active:scale-95 rounded font-bold text-slate-700 border border-slate-200 flex items-center justify-center text-sm transition"
                              title="Надясно (+5%)"
                            >
                              →
                            </button>
                          </div>
                          <div className="flex justify-between text-[9px] text-slate-400 mt-0.5 px-0.5 font-mono">
                            <span>0% (Наляво)</span>
                            <span>50% (Център)</span>
                            <span>100% (Надясно)</span>
                          </div>
                        </div>

                        {/* Vertical Pan Y */}
                        <div>
                          <div className="flex justify-between items-center text-[10px] mb-1 text-slate-600 uppercase tracking-wider font-bold">
                            <span>Вертикално изместване (горе / долу):</span>
                            <span className="font-mono font-bold text-[#C5A46E]">{agentPanY}%</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setAgentPanY(prev => Math.max(0, prev - 5))}
                              className="w-7 h-7 bg-white hover:bg-slate-200 active:scale-95 rounded font-bold text-slate-700 border border-slate-200 flex items-center justify-center text-sm transition"
                              title="Нагоре / Лице (-5%)"
                            >
                              ↑
                            </button>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={agentPanY}
                              onChange={(e) => setAgentPanY(parseInt(e.target.value))}
                              className="w-full accent-[#C5A46E] cursor-pointer"
                            />
                            <button
                              type="button"
                              onClick={() => setAgentPanY(prev => Math.min(100, prev + 5))}
                              className="w-7 h-7 bg-white hover:bg-slate-200 active:scale-95 rounded font-bold text-slate-700 border border-slate-200 flex items-center justify-center text-sm transition"
                              title="Надолу / Тяло (+5%)"
                            >
                              ↓
                            </button>
                          </div>
                          <div className="flex justify-between text-[9px] text-slate-400 mt-0.5 px-0.5 font-mono">
                            <span>0% (Горе / Глава & Лице)</span>
                            <span>50% (Център)</span>
                            <span>100% (Долу / Тяло)</span>
                          </div>

                          {/* Quick presets for Pan Y */}
                          <div className="flex flex-wrap gap-1.5 mt-1.5">
                            <button
                              type="button"
                              onClick={() => setAgentPanY(15)}
                              className={`px-2 py-0.5 rounded text-[10px] font-semibold transition border ${
                                agentPanY === 15 ? 'bg-[#C5A46E] text-white border-[#C5A46E]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
                              }`}
                            >
                              👤 Фокус Лице / Горе (15%)
                            </button>
                            <button
                              type="button"
                              onClick={() => setAgentPanY(50)}
                              className={`px-2 py-0.5 rounded text-[10px] font-semibold transition border ${
                                agentPanY === 50 ? 'bg-[#0F1E32] text-white border-[#0F1E32]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
                              }`}
                            >
                              👔 Центрирано (50%)
                            </button>
                            <button
                              type="button"
                              onClick={() => setAgentPanY(85)}
                              className={`px-2 py-0.5 rounded text-[10px] font-semibold transition border ${
                                agentPanY === 85 ? 'bg-[#C5A46E] text-white border-[#C5A46E]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
                              }`}
                            >
                              🧍 Тяло / Долу (85%)
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column: 1:1 Live Website Card Preview (col-span-5) */}
                  <div className="md:col-span-5 flex flex-col items-center justify-start bg-slate-100/70 p-4 sm:p-5 rounded-2xl border border-slate-200/90 md:sticky md:top-4 self-start">
                    <AgentCardLivePreview
                      name={agentName}
                      role={agentRole}
                      phone={agentPhone}
                      email={agentEmail}
                      image={agentImage}
                      category={agentCategory}
                      zoom={agentZoom}
                      panX={agentPanX}
                      panY={agentPanY}
                      fit={agentFit}
                      onPanChange={(newX, newY) => {
                        setAgentPanX(newX);
                        setAgentPanY(newY);
                      }}
                      onZoomChange={(newZoom) => setAgentZoom(newZoom)}
                      onFitChange={(newFit) => setAgentFit(newFit)}
                      onReset={() => {
                        setAgentZoom(100);
                        setAgentPanX(50);
                        setAgentPanY(50);
                        setAgentFit('cover');
                      }}
                    />
                  </div>
                </div>

                {/* Form Footer Buttons */}
                <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 flex justify-between items-center shrink-0">
                  <span className="text-[11px] text-slate-400">
                    💡 Промените по мащаба и позицията се отразяват на живо в сайта.
                  </span>
                  <div className="flex gap-2.5">
                    <button
                      id="btn-agent-form-cancel"
                      type="button"
                      onClick={() => setIsAgentFormOpen(false)}
                      className="px-4 py-2 text-slate-600 hover:text-[#0F1E32] font-semibold text-xs rounded-lg hover:bg-slate-200 transition focus:outline-none"
                    >
                      Отказ
                    </button>
                    <button
                      id="btn-agent-form-save"
                      type="submit"
                      className="px-6 py-2 bg-[#0F1E32] hover:bg-[#C5A46E] text-white font-bold rounded-xl text-xs uppercase tracking-widest transition shadow-md flex items-center gap-1.5"
                    >
                      <Check className="w-4 h-4 text-emerald-300" />
                      Запиши служителя ✅
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {activeAdminTab === 'properties' ? (
          <>
            {/* Reordering helper banner */}
            <div className="mb-4 bg-amber-50/80 border border-amber-200 rounded-xl p-3.5 flex items-center justify-between text-xs text-[#8C6221]">
              <div className="flex items-center gap-2">
                <span className="text-base">↕️</span>
                <span>
                  <strong>Наместване на реда:</strong> Използвайте стрелките <strong>↑ ↓</strong> или кликнете върху номера <strong>#</strong>, за да преместите офертата на точна позиция. Промените се запазват автоматично и определят коя оферта след коя се показва на началната страница и в каталога.
                </span>
              </div>
            </div>

            {/* List of currently indexed properties managed in table */}
            <div className="bg-white rounded-2xl shadow border border-slate-150 overflow-hidden">
              <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-slate-100/40">
                <h3 className="font-bold text-[#0F1E32] text-sm tracking-wider uppercase">Списък с Вашите оферти ({apartments.length})</h3>
                <span className="text-[11px] bg-[#0F1E32] text-[#C5A46E] px-2.5 py-1 rounded font-bold uppercase">Управление на имоти</span>
              </div>

              <div className="overflow-x-auto w-full">
                <table className="w-full text-left border-collapse text-xs text-[#0F1E32] font-sans">
                  <thead>
                    <tr className="bg-slate-50 border-b border-gray-100 text-[#0F1E32]/70 font-semibold uppercase text-[10px] tracking-wider">
                      <th className="py-3 px-3 w-24 text-center">Ред</th>
                      <th className="py-3 px-3">Снимка</th>
                      <th className="py-3 px-3">Реф. ID</th>
                      <th className="py-3 px-4 w-44">Име / Локация</th>
                      <th className="py-3 px-3">Тип</th>
                      <th className="py-3 px-3">Статус</th>
                      <th className="py-3 px-3">Параметри</th>
                      <th className="py-3 px-3">Цена (€)</th>
                      <th className="py-3 px-4 text-right">Действия</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {apartments.filter(a => a.isActive !== false).length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center py-10 text-slate-400 font-medium">
                          Няма активни оферти в сайта и админ панела. Моля, добавете нов имот от бутона по-горе.
                        </td>
                      </tr>
                    ) : (
                      apartments.filter(a => a.isActive !== false).map((apt, idx) => (
                        <tr key={apt.id} className="hover:bg-slate-50 transition-colors">
                          <td className="py-3.5 px-2 text-center whitespace-nowrap">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                type="button"
                                onClick={() => handleMovePropertyUp(idx)}
                                disabled={idx === 0}
                                className="p-1 rounded text-slate-500 hover:text-[#0F1E32] hover:bg-slate-200 transition disabled:opacity-20 cursor-pointer disabled:cursor-not-allowed"
                                title="Премести нагоре"
                              >
                                <ArrowUp className="w-3.5 h-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const pos = window.prompt(`Задайте нова позиция за този имот (1 - ${apartments.filter(a => a.isActive !== false).length}):`, String(idx + 1));
                                  if (pos) {
                                    const num = parseInt(pos, 10);
                                    if (!isNaN(num) && num >= 1 && num <= apartments.filter(a => a.isActive !== false).length) {
                                      handleSetPropertyOrder(idx, num);
                                    }
                                  }
                                }}
                                className="font-mono text-[11px] font-bold text-[#0F1E32] bg-slate-100 hover:bg-[#C5A46E] hover:text-white px-1.5 py-0.5 rounded transition cursor-pointer"
                                title="Кликнете за директно задаване на номер на позиция"
                              >
                                #{idx + 1}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleMovePropertyDown(idx)}
                                disabled={idx === apartments.filter(a => a.isActive !== false).length - 1}
                                className="p-1 rounded text-slate-500 hover:text-[#0F1E32] hover:bg-slate-200 transition disabled:opacity-20 cursor-pointer disabled:cursor-not-allowed"
                                title="Премести надолу"
                              >
                                <ArrowDown className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                          <td className="py-3.5 px-3 shrink-0">
                            <img
                              src={apt.image}
                              alt={typeof apt.title === 'object' ? (apt.title as any)?.name || 'Имот' : apt.title}
                              referrerPolicy="no-referrer"
                              className="w-16 h-12 object-cover rounded-lg border border-slate-100 shadow-sm"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=100&q=80';
                              }}
                            />
                          </td>
                          <td className="py-3.5 px-3 font-mono font-bold text-slate-500">
                            #{apt.id}
                          </td>
                          <td className="py-3.5 px-4">
                            <div className="font-bold text-slate-800 break-words max-w-[180px]">{typeof apt.title === 'object' ? (apt.title as any)?.name || 'Имот' : apt.title}</div>
                            <div className="text-[10px] text-[#C5A46E] font-medium">{typeof apt.neighborhood === 'object' ? (apt.neighborhood as any)?.name || 'Варна' : (apt.neighborhood || 'Варна')}, Варна</div>
                          </td>
                          <td className="py-3.5 px-3">
                            <span className={`px-2 py-0.5 rounded text-[9px] font-bold tracking-wider uppercase inline-block text-white ${
                              apt.type === 'sale' ? 'bg-[#C5A46E]' : 'bg-[#1D608A]'
                            }`}>
                              {apt.type === 'sale' ? 'Продажба' : 'Наем'}
                            </span>
                          </td>
                          <td className="py-3.5 px-3">
                            <span className="px-2 py-0.5 rounded-full text-[9px] font-extrabold uppercase bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                              В сайта
                            </span>
                          </td>
                          <td className="py-3.5 px-3 text-slate-600 font-sans">
                            {(() => {
                              const estLower = String(apt.estateType || '').toLowerCase();
                              const titLower = (typeof apt.title === 'object' ? (apt.title as any)?.name || '' : String(apt.title || '')).toLowerCase();
                              const isPlotRow = estLower.includes('парцел') || estLower.includes('упи') || estLower.includes('земедел') || estLower.includes('терен') || titLower.includes('парцел') || titLower.includes('упи') || titLower.includes('терен');
                              const isGarageRow = !isPlotRow && (estLower.includes('гараж') || estLower.includes('паркомяст') || titLower.includes('гараж') || titLower.includes('паркомяст') || (apt.area > 0 && apt.area <= 45 && typeof apt.description === 'string' && apt.description.toLowerCase().includes('гараж')));
                              const displayType = isPlotRow ? 'Парцел' : isGarageRow ? 'Гараж' : (apt.estateType || (apt.rooms > 0 ? `${apt.rooms}ст` : 'Имот'));
                              const displayFloor = isPlotRow ? 'Терен' : isGarageRow ? 'Партер' : `ет.${typeof apt.floor === 'object' ? (apt.floor as any)?.name || '1' : (apt.floor || '1')}`;
                              return (
                                <div>
                                  <div className="font-semibold text-slate-700">{apt.area} m² • {displayType} • {displayFloor}</div>
                                </div>
                              );
                            })()}
                          </td>
                          <td className="py-3.5 px-3 font-bold text-[#0F1E32]">
                            {apt.price.toLocaleString('bg-BG')} €
                          </td>
                          <td className="py-3.5 px-4 text-right space-x-1 whitespace-nowrap">
                            {/* Свали оферта (сваля и премахва напълно от сайта и админ панела) */}
                            <button
                              type="button"
                              id={`toggle-status-${apt.id}`}
                              onClick={() => {
                                setConfirmModal({
                                  title: 'Сваляне на оферта',
                                  message: `Сигурни ли сте, че искате да свалите и премахнете оферта #${apt.id} (${typeof apt.title === 'object' ? (apt.title as any)?.name || 'Имот' : apt.title}) изцяло от сайта и от админ панела?`,
                                  onConfirm: () => onDeleteProperty(apt.id)
                                });
                              }}
                              className="p-1.5 rounded transition cursor-pointer text-amber-700 hover:text-white bg-amber-50 hover:bg-amber-600"
                              title="Свали оферта (премахва се напълно от сайта и админ панела)"
                            >
                              <EyeOff className="w-3.5 h-3.5" />
                            </button>

                            <button
                              id={`star-hero-${apt.id}`}
                              onClick={async () => {
                                const isCurrentHero = siteContent.hero?.featuredPropertyId === apt.id || 
                                                      siteContent.hero?.featuredPropertyId === apt.estateId ||
                                                      (String(apt.estateId) && String(siteContent.hero?.featuredPropertyId) === String(apt.estateId));
                                updateSiteSection('hero', { featuredPropertyId: isCurrentHero ? '' : apt.id });
                                await saveSiteContent();
                              }}
                              className={`p-1.5 rounded transition cursor-pointer ${
                                (siteContent.hero?.featuredPropertyId === apt.id || siteContent.hero?.featuredPropertyId === apt.estateId)
                                  ? 'bg-amber-100 text-amber-700 font-bold border border-amber-300 shadow-sm'
                                  : 'text-slate-400 hover:text-amber-600 bg-slate-50 hover:bg-amber-50'
                              }`}
                              title={(siteContent.hero?.featuredPropertyId === apt.id || siteContent.hero?.featuredPropertyId === apt.estateId) ? "Премахни от Херото" : "Постави на Херото"}
                            >
                              <Star className={`w-3.5 h-3.5 ${(siteContent.hero?.featuredPropertyId === apt.id || siteContent.hero?.featuredPropertyId === apt.estateId) ? 'fill-amber-500 text-amber-500' : ''}`} />
                            </button>
                            <button
                              id={`edit-item-${apt.id}`}
                              onClick={() => handleOpenEditForm(apt)}
                              className="p-1.5 text-blue-600 hover:text-white bg-blue-50 hover:bg-blue-600 rounded transition cursor-pointer"
                              title="Редактирай"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              id={`delete-item-${apt.id}`}
                              onClick={() => {
                                setConfirmModal({
                                  title: 'Изтриване на имот',
                                  message: `Сигурни ли сте, че искате да изтриете имота: "${apt.title}"?`,
                                  onConfirm: () => onDeleteProperty(apt.id)
                                });
                              }}
                              className="p-1.5 text-red-600 hover:text-white bg-red-50 hover:bg-red-600 rounded transition cursor-pointer"
                              title="Изтрий"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Guidance section */}
            <div className="mt-8 bg-blue-50 border-l-4 border-blue-600 rounded-r-xl p-5 text-slate-700 font-sans">
              <h4 className="font-bold text-sm text-[#0F1E32] flex items-center gap-1.5">
                Как работи качването?
              </h4>
              <p className="text-xs mt-1.5 leading-relaxed">
                Всички добавени, променени или изтрити имоти се синхронизират сигурно с облачната база данни и се запазват локално на Вашето устройство.
                Те веднага се отразяват в разделите <strong>Продажби</strong>, <strong>Наеми</strong>, в подробното търсене, заглавната страница и в картотеката.
              </p>
            </div>
          </>
        ) : activeAdminTab === 'agents' ? (
          <>
            {/* List of currently managed employees in table */}
            <div className="bg-white rounded-2xl shadow border border-slate-150 overflow-hidden">
              <div className="p-5 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-slate-100/40">
                <div>
                  <h3 className="font-bold text-[#0F1E32] text-sm tracking-wider uppercase flex items-center gap-2">
                    <ListOrdered className="w-4 h-4 text-[#C5A46E]" />
                    Списък с Вашия екип ({agents.length})
                  </h3>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Редът тук е точно редът, в който брокерите се виждат в сайта (от 1-ви до последен).
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] bg-amber-50 text-[#8C6221] border border-amber-200/80 px-2.5 py-1 rounded-md font-bold uppercase flex items-center gap-1">
                    <span>↕️ Подредба с 1 клик</span>
                  </span>
                  <span className="text-[11px] bg-[#0F1E32] text-[#C5A46E] px-2.5 py-1 rounded font-bold uppercase">
                    Управление на екип
                  </span>
                </div>
              </div>

              <div className="overflow-x-auto w-full">
                <table className="w-full text-left border-collapse text-xs text-[#0F1E32] font-sans">
                  <thead>
                    <tr className="bg-slate-50 border-b border-gray-100 text-[#0F1E32]/70 font-semibold uppercase text-[10px] tracking-wider">
                      <th className="py-3 px-3 w-48 text-center">Ред в сайта</th>
                      <th className="py-3 px-4">Служител</th>
                      <th className="py-3 px-4">Позиция / Роля</th>
                      <th className="py-3 px-4">Категория</th>
                      <th className="py-3 px-4">Телефон</th>
                      <th className="py-3 px-4">Имейл</th>
                      <th className="py-3 px-4 text-right">Действие</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {agents.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-10 text-slate-400 font-medium">
                          Няма намерени служители. Моля, добавете нов служител от бутона по-горе.
                        </td>
                      </tr>
                    ) : (
                      agents.map((agent, index) => {
                        const isFirst = index === 0;
                        const isLast = index === agents.length - 1;
                        const isDragged = draggedAgentIndex === index;
                        const isOver = dragOverIndex === index;

                        return (
                          <tr 
                            key={agent.id}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', String(index));
                              setDraggedAgentIndex(index);
                            }}
                            onDragOver={(e) => {
                              e.preventDefault();
                              if (dragOverIndex !== index) {
                                setDragOverIndex(index);
                              }
                            }}
                            onDragLeave={() => {
                              if (dragOverIndex === index) {
                                setDragOverIndex(null);
                              }
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              const sourceIdxStr = e.dataTransfer.getData('text/plain');
                              const sourceIdx = parseInt(sourceIdxStr, 10);
                              if (!isNaN(sourceIdx) && sourceIdx !== index) {
                                handleMoveAgent(sourceIdx, index);
                              }
                              setDraggedAgentIndex(null);
                              setDragOverIndex(null);
                            }}
                            onDragEnd={() => {
                              setDraggedAgentIndex(null);
                              setDragOverIndex(null);
                            }}
                            className={`transition-all select-none ${
                              isDragged ? 'opacity-30 bg-amber-50' : 
                              isOver ? 'bg-amber-100/70 border-t-2 border-[#C5A46E]' : 
                              'hover:bg-slate-50/80'
                            }`}
                          >
                            {/* Order Controls Column */}
                            <td className="py-3 px-3">
                              <div className="flex items-center justify-center gap-1.5">
                                {/* Drag Handle */}
                                <span 
                                  className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-800 p-1 rounded hover:bg-slate-200 transition" 
                                  title="Хванете и плъзнете (Drag & Drop) за пренареждане"
                                >
                                  <GripVertical className="w-4 h-4" />
                                </span>

                                {/* Quick position select dropdown */}
                                <select
                                  value={index + 1}
                                  onChange={(e) => handleMoveAgent(index, parseInt(e.target.value) - 1)}
                                  className="bg-white border border-slate-200 hover:border-[#C5A46E] rounded-md px-2 py-1 text-xs font-bold text-[#0F1E32] focus:ring-1 focus:ring-[#C5A46E] focus:outline-none cursor-pointer shadow-2xs"
                                  title="Изберете директен номер на позиция"
                                >
                                  {agents.map((_, i) => (
                                    <option key={i + 1} value={i + 1}>
                                      #{i + 1}{i === 0 ? ' (1-ви)' : i === agents.length - 1 ? ' (Последен)' : ''}
                                    </option>
                                  ))}
                                </select>

                                {/* Step by step buttons */}
                                <div className="flex items-center gap-0.5">
                                  <button
                                    type="button"
                                    onClick={() => handleMoveAgentUp(index)}
                                    disabled={isFirst}
                                    className="p-1 rounded text-slate-600 hover:text-white bg-slate-100 hover:bg-[#0F1E32] disabled:opacity-20 disabled:pointer-events-none transition cursor-pointer"
                                    title="Премести 1 позиция нагоре (по-напред)"
                                  >
                                    <ArrowUp className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleMoveAgentDown(index)}
                                    disabled={isLast}
                                    className="p-1 rounded text-slate-600 hover:text-white bg-slate-100 hover:bg-[#0F1E32] disabled:opacity-20 disabled:pointer-events-none transition cursor-pointer"
                                    title="Премести 1 позиция надолу (по-назад)"
                                  >
                                    <ArrowDown className="w-3.5 h-3.5" />
                                  </button>
                                </div>

                                {/* Quick to top button */}
                                {!isFirst && (
                                  <button
                                    type="button"
                                    onClick={() => handleMoveAgentToTop(index)}
                                    className="px-1.5 py-0.5 rounded text-[10px] font-extrabold text-[#C5A46E] hover:text-white bg-amber-50 hover:bg-[#C5A46E] border border-amber-200/60 transition cursor-pointer"
                                    title="Направи 1-ви в сайта (най-отгоре)"
                                  >
                                    ТОП
                                  </button>
                                )}
                              </div>
                            </td>

                            {/* Agent Photo & Name */}
                            <td className="py-3.5 px-4 flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full overflow-hidden border border-slate-200 shadow-sm shrink-0 bg-slate-100 relative flex items-center justify-center">
                                <img
                                  src={agent.image}
                                  alt={agent.name}
                                  referrerPolicy="no-referrer"
                                  className="w-full h-full relative z-10"
                                  style={getPositionedImageStyle(
                                    agent.imageZoom,
                                    agent.imagePanX,
                                    agent.imagePanY,
                                    agent.imageFit
                                  )}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).src = DEFAULT_AGENT_AVATAR;
                                  }}
                                />
                              </div>
                              <div>
                                <div className="font-bold text-slate-800 text-sm">{agent.name}</div>
                                <div className="text-[10px] text-slate-400 font-mono">
                                  Позиция #{index + 1} в сайта
                                </div>
                              </div>
                            </td>
                            <td className="py-3.5 px-4 font-medium text-slate-700">
                              {agent.role}
                            </td>
                            <td className="py-3.5 px-4">
                              <span className={`px-2.5 py-0.5 rounded text-[9px] font-bold tracking-wider uppercase inline-block text-white ${
                                agent.category === 'sales' ? 'bg-[#C5A46E]' : agent.category === 'rentals' ? 'bg-[#1D608A]' : 'bg-slate-600'
                              }`}>
                                {agent.category === 'sales' ? 'Продажби' : agent.category === 'rentals' ? 'Наеми' : 'Офис'}
                              </span>
                            </td>
                            <td className="py-3.5 px-4 text-slate-600 font-sans font-medium whitespace-nowrap">
                              {agent.phone}
                            </td>
                            <td className="py-3.5 px-4 text-slate-500 font-mono italic">
                              {agent.email}
                            </td>
                            <td className="py-3.5 px-4 text-right space-x-1.5 whitespace-nowrap font-sans">
                              <button
                                id={`edit-agent-${agent.id}`}
                                onClick={() => handleOpenEditAgentForm(agent)}
                                className="p-1.5 text-blue-600 hover:text-white bg-blue-50 hover:bg-blue-600 rounded transition cursor-pointer"
                                title="Редактирай"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                id={`delete-agent-${agent.id}`}
                                onClick={() => {
                                  setConfirmModal({
                                    title: 'Изтриване на служител',
                                    message: `Сигурни ли сте, че искате да изтриете служителя: "${agent.name}"?`,
                                    onConfirm: () => onDeleteAgent(agent.id)
                                  });
                                }}
                                className="p-1.5 text-red-600 hover:text-white bg-red-50 hover:bg-red-600 rounded transition cursor-pointer"
                                title="Изтрий"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Agent Guidance section */}
            <div className="mt-8 bg-amber-50 border-l-4 border-amber-600 rounded-r-xl p-5 text-slate-700 font-sans">
              <h4 className="font-bold text-sm text-[#0F1E32] flex items-center gap-1.5">
                <ListOrdered className="w-4 h-4 text-amber-600" />
                Как работи редът и подредбата на екипа?
              </h4>
              <p className="text-xs mt-1.5 leading-relaxed">
                С колоната <strong>„Ред в сайта“</strong> можете с 1 клик да определяте кой служител да бъде първи, втори или последен.
                Можете да пренареждате със стрелките (<strong>↑</strong> / <strong>↓</strong>), с бутона <strong>ТОП</strong> за директно качване на 1-ва позиция, чрез падащото меню за точен номер, с <strong>влачене (Drag & Drop)</strong> на реда, или от формата за редакция.
                Всички промени се запазват автоматично и се отразяват на живо в публичната страница <strong>Екип</strong>!
              </p>
            </div>
          </>
        ) : activeAdminTab === 'images' ? (
          <div className="space-y-6">
            {/* Sub-tabs: Public Media Library vs Live Crop Adjuster */}
            <div className="flex bg-white p-1.5 rounded-2xl border border-slate-200 gap-2 max-w-fit shadow-xs">
              <button
                type="button"
                onClick={() => setMediaTabMode('public_library')}
                className={`px-4 py-2.5 text-xs font-bold uppercase rounded-xl transition-all flex items-center gap-2 cursor-pointer ${
                  mediaTabMode === 'public_library'
                    ? 'bg-[#0F1E32] text-white shadow-sm'
                    : 'text-slate-600 hover:text-[#0F1E32] hover:bg-slate-50'
                }`}
              >
                <HardDrive className="w-4 h-4 text-[#C5A46E]" />
                Файлове в public/ (Медийна библиотека)
              </button>
              <button
                type="button"
                onClick={() => setMediaTabMode('adjust_crops')}
                className={`px-4 py-2.5 text-xs font-bold uppercase rounded-xl transition-all flex items-center gap-2 cursor-pointer ${
                  mediaTabMode === 'adjust_crops'
                    ? 'bg-[#0F1E32] text-white shadow-sm'
                    : 'text-slate-600 hover:text-[#0F1E32] hover:bg-slate-50'
                }`}
              >
                <ImageIcon className="w-4 h-4 text-[#C5A46E]" />
                Напасване на снимки на имоти & екип
              </button>
            </div>

            {mediaTabMode === 'public_library' ? (
              <MediaLibrary />
            ) : (
              <div className="space-y-6">
                {/* Sub-filters for images */}
                <div className="flex bg-white p-1.5 rounded-xl border border-slate-150 inline-flex gap-1">
                  {(['all', 'properties', 'agents'] as const).map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setImageFilter(filter)}
                      className={`px-4 py-2 text-xs font-bold uppercase rounded-lg transition-all focus:outline-none ${
                        imageFilter === filter
                          ? 'bg-[#0F1E32] text-white shadow-sm'
                          : 'text-slate-600 hover:text-[#0F1E32] hover:bg-slate-50'
                      }`}
                    >
                      {filter === 'all' ? 'Всички снимки 📂' : filter === 'properties' ? 'Имоти 🏠' : 'Екип 👥'}
                    </button>
                  ))}
                </div>

                {/* Photo list grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {/* Properties */}
                  {(imageFilter === 'all' || imageFilter === 'properties') &&
                    apartments.map((apt) => {
                      const zoom = (apt.imageZoom !== undefined ? apt.imageZoom : 100) / 100;
                      const panX = apt.imagePanX !== undefined ? apt.imagePanX : 50;
                      const panY = apt.imagePanY !== undefined ? apt.imagePanY : 50;
                      return (
                        <div key={apt.id} className="bg-white rounded-2xl border border-slate-150 overflow-hidden shadow-sm hover:shadow-md transition flex flex-col h-full">
                          <div className="relative aspect-video bg-slate-100 overflow-hidden flex items-center justify-center">
                            <img
                              src={apt.image}
                              alt=""
                              aria-hidden="true"
                              className="absolute inset-0 w-full h-full object-cover blur-sm scale-110 opacity-30 pointer-events-none"
                            />
                            <img
                              src={apt.image}
                              alt={apt.title}
                              referrerPolicy="no-referrer"
                              className="w-full h-full object-cover relative z-10"
                              style={{
                                transform: `scale(${zoom})`,
                                objectPosition: `${panX}% ${panY}%`,
                                transformOrigin: `${panX}% ${panY}%`
                              }}
                            />
                            <span className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded text-[9px] font-black uppercase bg-[#C5A46E] text-white tracking-wider z-20">
                              Имот
                            </span>
                            <span className="absolute bottom-2.5 right-2.5 px-2 py-0.5 rounded text-[9px] font-mono bg-[#0F1E32]/80 text-slate-300 font-bold z-20">
                              #{apt.id}
                            </span>
                          </div>
                          <div className="p-4 flex flex-col flex-grow">
                            <h4 className="font-bold text-slate-800 text-xs tracking-tight line-clamp-1">{apt.title}</h4>
                            <p className="text-[10px] text-[#C5A46E] font-medium mt-0.5">{apt.neighborhood}, Варна</p>
                            
                            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-400 font-mono mt-auto">
                              <span>Мащаб: {apt.imageZoom !== undefined ? apt.imageZoom : 100}%</span>
                              <span>X: {panX}%, Y: {panY}%</span>
                            </div>

                            <div className="flex gap-2 mt-3.5">
                              <button
                                onClick={() => setAdjustingItem({
                                  type: 'property',
                                  id: apt.id,
                                  title: apt.title,
                                  image: apt.image,
                                  zoom: apt.imageZoom !== undefined ? apt.imageZoom : 100,
                                  panX: panX,
                                  panY: panY
                                })}
                                className="flex-grow py-2 bg-slate-100 hover:bg-[#C5A46E] hover:text-white text-[#0F1E32] font-extrabold rounded-lg text-[10px] uppercase tracking-wider transition flex items-center justify-center gap-1.5 focus:outline-none"
                              >
                                <Edit2 className="w-3 h-3" />
                                Редактирай изгледа
                              </button>
                              <button
                                onClick={() => {
                                  setConfirmModal({
                                    title: 'Изтриване на снимка',
                                    message: `Сигурни ли сте, че искате да изтриете снимката на имот "${apt.title}"? Тя ще бъде заменена с празен плейсхолдър.`,
                                    onConfirm: () => {
                                      onEditProperty({
                                        ...apt,
                                        image: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800&fit=crop&q=60',
                                        imageZoom: 100,
                                        imagePanX: 50,
                                        imagePanY: 50
                                      });
                                    }
                                  });
                                }}
                                className="px-3.5 py-2 bg-red-50 hover:bg-red-600 text-red-600 hover:text-white rounded-lg transition flex items-center justify-center focus:outline-none"
                                title="Изтрий снимката"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                  {/* Agents */}
                  {(imageFilter === 'all' || imageFilter === 'agents') &&
                    agents.map((agent) => {
                      const zoom = (agent.imageZoom !== undefined ? agent.imageZoom : 100) / 100;
                      const panX = agent.imagePanX !== undefined ? agent.imagePanX : 50;
                      const panY = agent.imagePanY !== undefined ? agent.imagePanY : 50;
                      return (
                        <div key={agent.id} className="bg-white rounded-2xl border border-slate-150 overflow-hidden shadow-sm hover:shadow-md transition flex flex-col h-full">
                          <div className="relative aspect-video bg-slate-50 flex items-center justify-center border-b border-slate-100 p-3">
                            <div className="w-24 h-32 rounded-xl overflow-hidden border border-slate-200 shadow-sm bg-slate-100 relative flex items-center justify-center">
                              <img
                                src={agent.image}
                                alt=""
                                aria-hidden="true"
                                className="absolute inset-0 w-full h-full object-cover blur-sm scale-110 opacity-30 pointer-events-none"
                              />
                              <img
                                src={agent.image}
                                alt={agent.name}
                                referrerPolicy="no-referrer"
                                className="w-full h-full object-cover relative z-10"
                                style={{
                                  transform: `scale(${zoom})`,
                                  objectPosition: `${panX}% ${panY}%`,
                                  transformOrigin: `${panX}% ${panY}%`
                                }}
                              />
                            </div>
                            <span className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded text-[9px] font-black uppercase bg-[#1D608A] text-white tracking-wider z-20">
                              Служител
                            </span>
                          </div>
                          <div className="p-4 flex flex-col flex-grow">
                            <h4 className="font-bold text-slate-800 text-xs tracking-tight">{agent.name}</h4>
                            <p className="text-[10px] text-slate-500 font-medium mt-0.5">{agent.role}</p>
                            
                            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-400 font-mono mt-auto">
                              <span>Мащаб: {agent.imageZoom !== undefined ? agent.imageZoom : 100}%</span>
                              <span>X: {panX}%, Y: {panY}%</span>
                            </div>

                            <div className="flex gap-2 mt-3.5">
                              <button
                                onClick={() => setAdjustingItem({
                                  type: 'agent',
                                  id: agent.id,
                                  title: agent.name,
                                  image: agent.image,
                                  zoom: agent.imageZoom !== undefined ? agent.imageZoom : 100,
                                  panX: panX,
                                  panY: panY
                                })}
                                className="flex-grow py-2 bg-slate-100 hover:bg-[#C5A46E] hover:text-white text-[#0F1E32] font-extrabold rounded-lg text-[10px] uppercase tracking-wider transition flex items-center justify-center gap-1.5 focus:outline-none"
                              >
                                <Edit2 className="w-3 h-3" />
                                Редактирай изгледа
                              </button>
                              <button
                                onClick={() => {
                                  setConfirmModal({
                                    title: 'Изтриване на снимка',
                                    message: `Сигурни ли сте, че искате да изтриете снимката на служител "${agent.name}"? Тя ще бъде заменена с дефолтен аватар.`,
                                    onConfirm: () => {
                                      onEditAgent({
                                        ...agent,
                                        image: DEFAULT_AGENT_AVATAR,
                                        imageZoom: 100,
                                        imagePanX: 50,
                                        imagePanY: 50
                                      });
                                    }
                                  });
                                }}
                                className="px-3.5 py-2 bg-red-50 hover:bg-red-600 text-red-600 hover:text-white rounded-lg transition flex items-center justify-center focus:outline-none"
                                title="Изтрий снимката"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>

                {/* Guidance section */}
                <div className="mt-8 bg-purple-50 border-l-4 border-purple-600 rounded-r-xl p-5 text-slate-700 font-sans">
                  <h4 className="font-bold text-sm text-[#0F1E32] flex items-center gap-1.5">
                    📏 Как работи регулирането на снимките?
                  </h4>
                  <p className="text-xs mt-1.5 leading-relaxed">
                    Когато качите снимка на имот или на Ваш колега, понякога лицето или фокусът на сградата не се центрира автоматично поради различно съотношение.
                    Тук можете бързо да зададете мащаб (зуум) и да преместите фокуса хоризонтално или вертикално. Промените веднага се отразяват на целия уебсайт!
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : activeAdminTab === 'content' ? (
          <div className="animate-fade-in">
            <SiteContentEditor apartments={apartments} />
          </div>
        ) : activeAdminTab === 'seo' ? (
          <div className="animate-fade-in">
            <SEOManager />
          </div>
        ) : activeAdminTab === 'inquiries' ? (
          <div className="animate-fade-in">
            <InquiriesManager />
          </div>
        ) : activeAdminTab === 'sync' ? (
          <div className="animate-fade-in">
            <SyncManager onSyncCompleted={onRefreshProperties} />
          </div>
        ) : (
          /* activeAdminTab === 'firebase' */
          <div className="animate-fade-in">
            <FirebaseManager 
              properties={apartments}
              agents={agents}
              onRefreshProperties={onRefreshProperties}
            />
          </div>
        )}
      </div>

      {/* Live Image Adjuster Modal */}
      {adjustingItem && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-2xl w-full overflow-hidden shadow-2xl border border-slate-100 font-sans flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="bg-[#0F1E32] text-white py-4 px-6 flex justify-between items-center shrink-0">
              <span className="font-bold text-xs tracking-wider uppercase text-[#C5A46E] flex items-center gap-2">
                <ImageIcon className="w-4 h-4" />
                Корекция на снимка: {adjustingItem.title}
              </span>
              <button
                onClick={() => setAdjustingItem(null)}
                className="text-slate-400 hover:text-white text-lg font-bold focus:outline-none"
              >
                &times;
              </button>
            </div>

            {/* Content body */}
            <div className="p-6 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left side: Live Preview */}
              <div className="flex flex-col items-center justify-center">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 self-start">Реално изрязване на сайта:</span>
                
                {adjustingItem.type === 'property' ? (
                  /* Property card layout: aspect-video (16:9) */
                  <div className="w-full aspect-video rounded-xl overflow-hidden border-2 border-[#C5A46E] shadow-md bg-slate-100 relative flex items-center justify-center">
                    <img
                      src={adjustingItem.image}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 w-full h-full object-cover blur-sm scale-110 opacity-30 pointer-events-none"
                    />
                    <img
                      src={adjustingItem.image}
                      alt="Property Preview"
                      referrerPolicy="no-referrer"
                      className="w-full h-full object-cover relative z-10 transition-transform duration-75"
                      style={{
                        transform: `scale(${adjustingItem.zoom / 100})`,
                        objectPosition: `${adjustingItem.panX}% ${adjustingItem.panY}%`,
                        transformOrigin: `${adjustingItem.panX}% ${adjustingItem.panY}%`
                      }}
                    />
                  </div>
                ) : (
                  /* Agent portrait card matching website layout: aspect-[3/4] / h-64 */
                  <div className="w-52 h-64 rounded-2xl overflow-hidden border-2 border-[#C5A46E] shadow-lg bg-slate-100 relative flex items-center justify-center">
                    <img
                      src={adjustingItem.image}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 w-full h-full object-cover blur-sm scale-110 opacity-30 pointer-events-none"
                    />
                    <img
                      src={adjustingItem.image}
                      alt="Agent Preview"
                      referrerPolicy="no-referrer"
                      className="w-full h-full relative z-10 transition-transform duration-75"
                      style={getPositionedImageStyle(
                        adjustingItem.zoom,
                        adjustingItem.panX,
                        adjustingItem.panY,
                        'cover'
                      )}
                    />
                  </div>
                )}

                <p className="text-[10px] text-slate-400 mt-3 text-center">
                  {adjustingItem.type === 'property' 
                    ? 'Визуализира се точно по начина, по който изглежда в картата на имота (16:9).' 
                    : 'Визуализира се точно по начина, по който изглежда в картата на служителя на сайта.'}
                </p>
              </div>

              {/* Right side: Sliders */}
              <div className="flex flex-col justify-center space-y-4">
                {/* Zoom slider */}
                <div>
                  <div className="flex justify-between items-center text-[11px] mb-1.5 text-slate-600 uppercase tracking-wider font-bold">
                    <span>Мащаб (Зуум / Отдалечаване):</span>
                    <span className="text-[#C5A46E] font-bold">
                      {adjustingItem.zoom}% {adjustingItem.zoom < 100 ? '(Отдалечено 🔍-)' : adjustingItem.zoom > 100 ? '(Приближено 🔍+)' : '(100%)'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setAdjustingItem(prev => prev ? { ...prev, zoom: Math.max(20, prev.zoom - 5) } : null)}
                      className="w-7 h-7 bg-slate-100 hover:bg-slate-200 active:scale-95 rounded font-bold text-slate-700 flex items-center justify-center text-sm transition"
                      title="Отдалечи (-5%)"
                    >
                      -
                    </button>
                    <input
                      type="range"
                      min="20"
                      max="300"
                      step="1"
                      value={adjustingItem.zoom}
                      onChange={(e) => setAdjustingItem(prev => prev ? { ...prev, zoom: parseInt(e.target.value) } : null)}
                      className="w-full accent-[#C5A46E] cursor-pointer"
                    />
                    <button
                      type="button"
                      onClick={() => setAdjustingItem(prev => prev ? { ...prev, zoom: Math.min(300, prev.zoom + 5) } : null)}
                      className="w-7 h-7 bg-slate-100 hover:bg-slate-200 active:scale-95 rounded font-bold text-slate-700 flex items-center justify-center text-sm transition"
                      title="Приближи (+5%)"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Pan X slider */}
                <div>
                  <div className="flex justify-between items-center text-[11px] mb-1.5 text-slate-600 uppercase tracking-wider font-bold">
                    <span>Хоризонтално изместване:</span>
                    <span className="text-[#C5A46E] font-bold">{adjustingItem.panX}%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setAdjustingItem(prev => prev ? { ...prev, panX: Math.max(0, prev.panX - 5) } : null)}
                      className="w-7 h-7 bg-slate-100 hover:bg-slate-200 active:scale-95 rounded font-bold text-slate-700 flex items-center justify-center text-sm transition"
                      title="Наляво (-5%)"
                    >
                      ←
                    </button>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={adjustingItem.panX}
                      onChange={(e) => setAdjustingItem(prev => prev ? { ...prev, panX: parseInt(e.target.value) } : null)}
                      className="w-full accent-[#C5A46E] cursor-pointer"
                    />
                    <button
                      type="button"
                      onClick={() => setAdjustingItem(prev => prev ? { ...prev, panX: Math.min(100, prev.panX + 5) } : null)}
                      className="w-7 h-7 bg-slate-100 hover:bg-slate-200 active:scale-95 rounded font-bold text-slate-700 flex items-center justify-center text-sm transition"
                      title="Надясно (+5%)"
                    >
                      →
                    </button>
                  </div>
                </div>

                {/* Pan Y slider */}
                <div>
                  <div className="flex justify-between items-center text-[11px] mb-1.5 text-slate-600 uppercase tracking-wider font-bold">
                    <span>Вертикално изместване (горе / долу):</span>
                    <span className="text-[#C5A46E] font-bold">{adjustingItem.panY}%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setAdjustingItem(prev => prev ? { ...prev, panY: Math.max(0, prev.panY - 5) } : null)}
                      className="w-7 h-7 bg-slate-100 hover:bg-slate-200 active:scale-95 rounded font-bold text-slate-700 flex items-center justify-center text-sm transition"
                      title="Нагоре (-5%)"
                    >
                      ↑
                    </button>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={adjustingItem.panY}
                      onChange={(e) => setAdjustingItem(prev => prev ? { ...prev, panY: parseInt(e.target.value) } : null)}
                      className="w-full accent-[#C5A46E] cursor-pointer"
                    />
                    <button
                      type="button"
                      onClick={() => setAdjustingItem(prev => prev ? { ...prev, panY: Math.min(100, prev.panY + 5) } : null)}
                      className="w-7 h-7 bg-slate-100 hover:bg-slate-200 active:scale-95 rounded font-bold text-slate-700 flex items-center justify-center text-sm transition"
                      title="Надолу (+5%)"
                    >
                      ↓
                    </button>
                  </div>
                </div>

                {/* Presets and Resets */}
                <div className="pt-2 border-t border-slate-100 flex flex-wrap gap-1.5">
                  <button
                    onClick={() => setAdjustingItem(prev => prev ? { ...prev, zoom: 50 } : null)}
                    className="px-2.5 py-1 bg-amber-50 hover:bg-amber-100 text-[#8C6221] rounded text-[10px] font-bold transition flex items-center gap-1"
                  >
                    🔍 50% Отдалечи
                  </button>
                  <button
                    onClick={() => setAdjustingItem(prev => prev ? { ...prev, zoom: 75 } : null)}
                    className="px-2.5 py-1 bg-amber-50 hover:bg-amber-100 text-[#8C6221] rounded text-[10px] font-bold transition flex items-center gap-1"
                  >
                    🔍 75% Отдалечи
                  </button>
                  <button
                    onClick={() => setAdjustingItem(prev => prev ? { ...prev, zoom: 100, panX: 50, panY: 50 } : null)}
                    className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 rounded text-[10px] font-semibold text-slate-600 transition"
                  >
                    100% Центрирай ⚙️
                  </button>
                  <button
                    onClick={() => setAdjustingItem(prev => prev ? { ...prev, zoom: 125 } : null)}
                    className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 rounded text-[10px] font-semibold text-slate-600 transition"
                  >
                    🔍+ 125% Приближи
                  </button>
                  <button
                    onClick={() => setAdjustingItem(prev => prev ? { ...prev, panY: 15 } : null)}
                    className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 rounded text-[10px] font-semibold text-slate-600 transition"
                  >
                    Фокус Лице / Горе ⬆️
                  </button>
                  <button
                    onClick={() => setAdjustingItem(prev => prev ? { ...prev, panY: 85 } : null)}
                    className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 rounded text-[10px] font-semibold text-slate-600 transition"
                  >
                    Тяло / Долу ⬇️
                  </button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-slate-50 px-6 py-4 border-t border-slate-100 flex justify-end gap-3 shrink-0">
              <button
                onClick={() => setAdjustingItem(null)}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 font-bold text-xs uppercase tracking-wider transition"
              >
                Отказ
              </button>
              <button
                onClick={() => {
                  if (adjustingItem.type === 'property') {
                    const prop = apartments.find(a => a.id === adjustingItem.id);
                    if (prop) {
                      onEditProperty({
                        ...prop,
                        imageZoom: adjustingItem.zoom,
                        imagePanX: adjustingItem.panX,
                        imagePanY: adjustingItem.panY
                      });
                    }
                  } else {
                    const ag = agents.find(a => a.id === adjustingItem.id);
                    if (ag) {
                      onEditAgent({
                        ...ag,
                        imageZoom: adjustingItem.zoom,
                        imagePanX: adjustingItem.panX,
                        imagePanY: adjustingItem.panY
                      });
                    }
                  }
                  setAdjustingItem(null);
                }}
                className="px-6 py-2.5 bg-[#0F1E32] hover:bg-[#C5A46E] text-white font-bold rounded-lg text-xs uppercase tracking-widest shadow transition"
              >
                Запази Промените ✅
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Confirmation Modal */}
      {confirmModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-[110] p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl border border-slate-100 font-sans animate-in fade-in zoom-in duration-150">
            <h3 className="text-lg font-extrabold text-[#0F1E32] mb-2 flex items-center gap-2">
              <span>⚠️</span> {confirmModal.title}
            </h3>
            <p className="text-xs text-slate-600 mb-6 leading-relaxed">
              {confirmModal.message}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmModal(null)}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 font-bold text-xs uppercase tracking-wider transition focus:outline-none"
              >
                Отказ
              </button>
              <button
                onClick={() => {
                  confirmModal.onConfirm();
                  setConfirmModal(null);
                }}
                className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg text-xs uppercase tracking-wider shadow transition focus:outline-none"
              >
                Потвърждавам
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Media Picker Modal from public/ */}
      <MediaPickerModal
        isOpen={mediaPicker.isOpen}
        onClose={() => setMediaPicker(prev => ({ ...prev, isOpen: false }))}
        onSelect={mediaPicker.onSelect}
        allowedType={mediaPicker.allowedType}
        title={mediaPicker.title}
      />
    </section>
  );
}
